import React, { useRef, useState, useLayoutEffect, ReactNode } from 'react';
import { Typography, TypographyProps, Tooltip } from '@mui/material';

interface ResponsiveTitleProps extends TypographyProps {
    children?: ReactNode;
    minFontSize?: number;
    maxFontSize?: number;
}

/**
 * Composant qui ajuste automatiquement la taille de la police pour que le texte tienne sur une seule ligne.
 * DEAD SIMPLE & ROBUST implementation:
 * 1. Render a hidden clone of the text with `whiteSpace: nowrap` and `maxFontSize` (or inherited).
 * 2. Measure the width of this clone (natural width).
 * 3. Measure the width of the container.
 * 4. Calculate ratio = containerWidth / naturalWidth.
 * 5. Apply new fontSize = naturalFontSize * ratio (clamped).
 * 
 * No binary search. No loops. Pure geometry.
 */
const ResponsiveTitle: React.FC<ResponsiveTitleProps> = ({
    children,
    minFontSize = 20,
    maxFontSize,
    style,
    variant,
    ...props
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLSpanElement>(null);
    const [fontSize, setFontSize] = useState<number | undefined>(undefined);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    // Extract text content for tooltip
    const textContent = typeof children === 'string'
        ? children
        : ((props.dangerouslySetInnerHTML?.__html as unknown as string)?.replace(/<[^>]*>?/gm, '') || "");

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const updateSize = () => {
            // 1. Get container width
            const containerWidth = container.offsetWidth;
            if (containerWidth <= 0) return;

            // 2. Get natural metrics from the hidden measure element
            const measureRect = measure.getBoundingClientRect();
            const naturalWidth = measureRect.width;

            // If the element has no width (e.g. empty string), do nothing
            if (naturalWidth <= 0) return;

            // Get the font size currently applied to the measure element (which acts as our "max" or "base")
            const computedStyle = window.getComputedStyle(measure);
            const naturalFontSize = parseFloat(computedStyle.fontSize);

            // 3. Calculate Scale
            // ratio > 1 means container is larger than text (text fits easily)
            // ratio < 1 means container is smaller (text needs shrinking)
            const ratio = containerWidth / naturalWidth;

            // 4. Calculate target font size
            let targetSize = naturalFontSize * ratio;

            // 5. Clamp
            // If text is too small, we force it to minFontSize and allow wrapping
            let shouldWrap = false;
            if (targetSize < minFontSize) {
                targetSize = Math.max(minFontSize, 12); // Ensure decently readable size when wrapped
                shouldWrap = true;
            } else if (maxFontSize && targetSize > maxFontSize) {
                targetSize = maxFontSize;
            } else if (!maxFontSize && ratio >= 1) {
                targetSize = naturalFontSize;
            }

            // 6. Update state
            setFontSize(prev => {
                if (prev === undefined && ratio >= 1 && !maxFontSize && !shouldWrap) return undefined;
                if (prev && Math.abs(prev - targetSize) < 0.5 && shouldWrap === isTooltipOpen) return prev;
                return targetSize;
            });

            // 7. Update wrapping state (reuses isTooltipOpen logic for now, or we can separate)
            // Actually, if we wrap, we DON'T need the tooltip because text is visible/readable.
            // But let's reuse a state or add one. 
            // The previous logic used `isTooltipOpen` to show tooltip if truncated/too small.
            // Here, if `shouldWrap` is true, we show full text wrapped. So no tooltip needed in that specific case?
            // Let's store the wrap state.
            setIsTooltipOpen(shouldWrap);
        };

        // Initial update
        updateSize();

        // Monitor for container resizing
        let rAFId: number;

        const scheduleUpdate = () => {
            if (rAFId) cancelAnimationFrame(rAFId);
            rAFId = requestAnimationFrame(updateSize);
        };

        const resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(container);

        // Also listen to window resize to catch maximize events more reliably
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            if (rAFId) cancelAnimationFrame(rAFId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, [children, minFontSize, maxFontSize, props.dangerouslySetInnerHTML, variant]); // Re-run if content or constraints change

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                overflow: 'hidden',
                // Important: maintain space even if empty to allow validation/structure
                minHeight: '1.2em',
                minWidth: 0, // Fix flexbox overflow issues
                position: 'relative', // Context for absolute positioning if needed (though we use raw text)
                ...style
            }}
        >
            {/* 
                HIDDEN MEASURE ELEMENT 
                Renders with the "intended" style (variant, maxFontSize) but no width constraint.
                Used solely to calculate "naturalWidth".
             */}
            <Typography
                component="span"
                ref={measureRef}
                variant={variant}
                {...props}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    whiteSpace: 'nowrap',
                    width: 'auto',
                    height: 'auto',
                    maxWidth: 'none', // Ensure it is not constrained by parent or props
                    left: 0,
                    top: 0,
                    fontSize: maxFontSize ? `${maxFontSize}px` : undefined, // Force max if set, else inherit variant
                    pointerEvents: 'none', // Ensure it doesn't block clicks
                }}
            >
                {children}
            </Typography>

            {/* 
                VISIBLE ELEMENT
                Applies the calculated fontSize.
             */}
            <Tooltip title={isTooltipOpen ? textContent : ""} disableHoverListener={!isTooltipOpen}>
                <Typography
                    component="span"
                    variant={variant}
                    {...props}
                    style={{
                        fontSize: fontSize ? `${fontSize}px` : undefined,
                        whiteSpace: isTooltipOpen ? 'normal' : 'nowrap', // Wrap if too small
                        textAlign: isTooltipOpen ? 'center' : undefined, // Center if wrapped
                        display: isTooltipOpen ? 'block' : 'inline-block', // Block for centering
                        lineHeight: isTooltipOpen ? 1.3 : 1.2,
                        transition: 'font-size 0.05s linear',
                        maxWidth: '100%',
                    }}
                >
                    {children}
                </Typography>
            </Tooltip>
        </div>
    );
};

export default ResponsiveTitle;
